import React, { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/AuthContext";
import { http } from "@/lib/api";

import HomePage from "@/pages/HomePage";
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ConfirmationPage = lazy(() => import("@/pages/ConfirmationPage"));
const MyBookingPage = lazy(() => import("@/pages/MyBookingPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const BibDistributionPortal = lazy(() => import("@/pages/BibDistributionPortal"));
const AffiliatePortal = lazy(() => import("@/pages/AffiliatePortal"));
const AdminApp = lazy(() => import("@/pages/admin/AdminApp"));

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0b1120]" data-testid="page-loading">
    <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-[#FF4D00] animate-spin" />
  </div>
);

function AuthCallback() {
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        if (sid) await http.post("/auth/session", { session_id: sid });
      } catch { /* ignore */ }
      window.history.replaceState(null, "", "/admin");
      navigate("/admin", { replace: true });
      window.location.reload();
    })();
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b1120] text-white">
      <p className="font-display text-3xl tracking-wide">Signing you in…</p>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/confirmation" element={<ConfirmationPage />} />
      <Route path="/mybooking/:bookingId" element={<MyBookingPage />} />
      <Route path="/qr/:token" element={<MyBookingPage />} />
      <Route path="/bib-distribution" element={<BibDistributionPortal />} />
      <Route path="/volunteer" element={<BibDistributionPortal />} />
      <Route path="/affiliate" element={<AffiliatePortal />} />
      <Route path="/influencer" element={<AffiliatePortal />} />
      <Route path="/admin/*" element={<AuthProvider><AdminApp /></AuthProvider>} />
      <Route path="/ops-portal/*" element={<AuthProvider><AdminApp /></AuthProvider>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors theme="dark" />
    </BrowserRouter>
  );
}
